import React, { useState, useEffect, useCallback } from 'react';
import './Compendium.css';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

/* ──── category badge colours ──── */
const CATEGORY_COLORS = {
  tutorial:       '#6baed6',
  main:           '#c9a84c',
  good:           '#4caf50',
  neutral:        '#b0a060',
  evil:           '#c0392b',
  side:           '#9b59b6',
  repeatable:     '#2ecc71',
  advanced_class: '#ffd700',
};

const CATEGORY_LABELS = {
  tutorial:       'Tutorial',
  main:           'Main Quest',
  good:           'Good Path',
  neutral:        'Neutral Path',
  evil:           'Evil Path',
  side:           'Side Quest',
  repeatable:     'Repeatable',
  advanced_class: '⚔️ Advanced Class',
};

const STATUS_ICONS = {
  active:       '📜',
  completed:    '✅',
  failed:       '❌',
  available:    '❗',
  undiscovered: '❔',
  locked:       '🔒',
};

const DISCIPLINE_ICONS = {
  smithing:    '⚒️',
  alchemy:     '⚗️',
  enchanting:  '✨',
};

const Compendium = ({ character, onClose, onOpenWorldMap, onOpenGuide, initialQuestFilter }) => {
  const [tab, setTab] = useState('quests');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Quests state ──
  // Default to caller-supplied filter so a player who opened the compendium because
  // of an "available quests" badge lands directly on the available list.
  const [questFilter, setQuestFilter] = useState(initialQuestFilter || 'all'); // all | active | available | completed | failed

  // ── Recipes state ──
  const [recipeSort, setRecipeSort] = useState('discipline'); // discipline | level | name
  const [recipeFilter, setRecipeFilter] = useState('all');    // all | smithing | alchemy | enchanting

  // ── Bestiary state ──
  const [expandedMonster, setExpandedMonster] = useState(null);

  const fetchCompendium = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/compendium`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load compendium');
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Compendium fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompendium();
  }, [fetchCompendium]);

  /* ================================================================
   *  QUEST LOG TAB
   * ================================================================ */
  const renderQuestLog = () => {
    if (!data?.questLog) return null;

    let quests = [...data.questLog];

    // Filter
    if (questFilter !== 'all') {
      quests = quests.filter(q => q.status === questFilter);
    }

    // Sort: active first, available, undiscovered, then completed (reverse order), then failed, then locked
    const statusOrder = { active: 0, available: 1, undiscovered: 2, completed: 3, failed: 4, locked: 5 };
    quests.sort((a, b) => {
      const so = statusOrder[a.status] - statusOrder[b.status];
      if (so !== 0) return so;
      if (a.status === 'completed') return b.order - a.order; // reverse for completed
      return a.order - b.order;
    });

    // Counts for filter badges
    const counts = { active: 0, available: 0, undiscovered: 0, completed: 0, failed: 0, locked: 0 };
    for (const q of data.questLog) counts[q.status] = (counts[q.status] || 0) + 1;

    return (
      <div className="comp-quest-tab">
        {/* "What triggered the badge?" banner — only shown when there are quests
            ready to pick up. Helps players understand why the compendium icon
            had a notification dot. */}
        {counts.available > 0 && (
          <div className="comp-quest-new-banner">
            <span className="comp-new-icon">❗</span>
            <div className="comp-new-text">
              <strong>{counts.available} quest{counts.available === 1 ? '' : 's'} ready to start</strong>
              <span className="comp-new-sub">
                Visit the listed quest giver in their location to begin.
              </span>
            </div>
            {questFilter !== 'available' && (
              <button
                className="comp-new-action"
                onClick={() => setQuestFilter('available')}
              >
                Show available
              </button>
            )}
          </div>
        )}

        {/* Filter bar */}
        <div className="comp-filter-bar">
          {['all', 'active', 'available', 'undiscovered', 'completed', 'failed'].map(f => (
            <button
              key={f}
              className={`comp-filter-btn ${questFilter === f ? 'active' : ''}`}
              onClick={() => setQuestFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && counts[f] > 0 && (
                <span className="comp-filter-count">{counts[f]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Quest map hint — shown when there are active quests */}
        {counts.active > 0 && (
          <div className="comp-quest-map-hint" onClick={onOpenWorldMap}>
            🗺️ Active quest destinations are highlighted on your <span className="comp-map-link">World Map</span>
          </div>
        )}

        {quests.length === 0 && (
          <div className="comp-empty">No quests match this filter.</div>
        )}

        <div className="comp-quest-list">
          {quests.map((q, i) => {
            const hideDetails = q.status === 'locked' || q.status === 'undiscovered';
            return (
            <div key={i} className={`comp-quest-card comp-quest-${q.status}`}>
              <div className="comp-quest-header">
                <span className="comp-quest-icon">{STATUS_ICONS[q.status] || '🔒'}</span>
                <span className="comp-quest-name">{hideDetails ? '???' : q.name}</span>
                <span
                  className="comp-quest-category"
                  style={{ color: CATEGORY_COLORS[q.category] || '#888' }}
                >
                  {CATEGORY_LABELS[q.category] || q.category}
                </span>
              </div>
              <div className="comp-quest-body">
                {!hideDetails && q.description && (
                  <div className="comp-quest-desc">{q.description}</div>
                )}
                <div className="comp-quest-meta">
                  {q.status === 'undiscovered' ? (
                    <span className="comp-quest-giver">📍 Unknown quest giver</span>
                  ) : (
                    <span className="comp-quest-giver">📍 {q.giver}</span>
                  )}
                  {q.levelReq > 0 && (
                    <span className="comp-quest-level">⚔️ Lv.{q.levelReq}+</span>
                  )}
                  {q.location && q.status !== 'locked' && (
                    <span className="comp-quest-location">🗺️ {q.location}</span>
                  )}
                </div>
                <div className="comp-quest-status-row">
                  <div className={`comp-quest-status comp-status-${q.status}`}>
                    {q.statusLabel}
                  </div>
                  {q.status === 'active' && q.destinationArea && onOpenWorldMap && (
                    <button
                      className="comp-quest-map-btn"
                      onClick={(e) => { e.stopPropagation(); onOpenWorldMap(); }}
                      title="Show quest destination on World Map"
                    >
                      🗺️ Show on Map
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================
   *  CRAFTING RECIPE BOOK TAB
   * ================================================================ */
  const renderRecipeBook = () => {
    if (!data?.recipes) return null;

    let recipes = [...data.recipes];

    // Filter by discipline
    if (recipeFilter !== 'all') {
      recipes = recipes.filter(r => r.discipline === recipeFilter);
    }

    // Sort
    recipes.sort((a, b) => {
      if (recipeSort === 'discipline') {
        const d = a.discipline.localeCompare(b.discipline);
        return d !== 0 ? d : a.level - b.level;
      }
      if (recipeSort === 'level') return a.level - b.level;
      return a.name.localeCompare(b.name);
    });

    // Discipline levels from crafting data
    const crafting = data.crafting || {};

    return (
      <div className="comp-recipe-tab">
        {/* Discipline levels summary */}
        <div className="comp-discipline-bar">
          {['smithing', 'alchemy', 'enchanting'].map(d => {
            const info = crafting[d] || { level: 1, xp: 0 };
            return (
              <div key={d} className="comp-discipline-badge">
                <span className="comp-disc-icon">{DISCIPLINE_ICONS[d]}</span>
                <span className="comp-disc-name">{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                <span className="comp-disc-level">Lv.{info.level}</span>
              </div>
            );
          })}
        </div>

        {/* Filter & sort */}
        <div className="comp-filter-bar">
          <div className="comp-filter-group">
            {['all', 'smithing', 'alchemy', 'enchanting'].map(f => (
              <button
                key={f}
                className={`comp-filter-btn ${recipeFilter === f ? 'active' : ''}`}
                onClick={() => setRecipeFilter(f)}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="comp-sort-group">
            <label>Sort:</label>
            <select value={recipeSort} onChange={e => setRecipeSort(e.target.value)}>
              <option value="discipline">Discipline</option>
              <option value="level">Level</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>

        <div className="comp-recipe-count">
          📖 {recipes.length} recipe{recipes.length !== 1 ? 's' : ''} discovered
        </div>

        {recipes.length === 0 && (
          <div className="comp-empty">No recipes match this filter.</div>
        )}

        <div className="comp-recipe-list">
          {recipes.map((r, i) => (
            <div key={i} className="comp-recipe-card">
              <div className="comp-recipe-header">
                <span className="comp-recipe-icon">{DISCIPLINE_ICONS[r.discipline]}</span>
                <span className="comp-recipe-name">{r.name}</span>
                <span className="comp-recipe-level">Lv.{r.level}</span>
              </div>
              <div className="comp-recipe-body">
                {r.outputDescription && (
                  <div className="comp-recipe-desc">{r.outputDescription}</div>
                )}
                <div className="comp-recipe-materials">
                  <span className="comp-recipe-label">Materials:</span>
                  {r.materials.map((m, j) => (
                    <span key={j} className="comp-recipe-mat">
                      {m.qty}× {m.name}
                    </span>
                  ))}
                </div>
                <div className="comp-recipe-footer">
                  <span className="comp-recipe-cost">💰 {r.goldCost}g</span>
                  <span className="comp-recipe-xp">⭐ {r.xpGain} XP</span>
                  <span className="comp-recipe-output">→ {r.outputName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ================================================================
   *  BESTIARY TAB
   * ================================================================ */
  const renderBestiary = () => {
    if (!data?.bestiary) return null;

    const { discovered, total } = data.bestiaryStats || { discovered: 0, total: 0 };

    return (
      <div className="comp-bestiary-tab">
        <div className="comp-bestiary-progress">
          <div className="comp-bestiary-bar-bg">
            <div
              className="comp-bestiary-bar-fill"
              style={{ width: `${total > 0 ? (discovered / total) * 100 : 0}%` }}
            />
          </div>
          <span className="comp-bestiary-count">
            🐉 {discovered} / {total} discovered
          </span>
        </div>

        <div className="comp-bestiary-list">
          {data.bestiary.map((m, i) => {
            if (!m.discovered) {
              // Unencountered — locked entry
              return (
                <div key={i} className="comp-beast-card comp-beast-locked">
                  <div className="comp-beast-header">
                    <span className="comp-beast-icon">❓</span>
                    <span className="comp-beast-name">???</span>
                  </div>
                </div>
              );
            }

            const isExpanded = expandedMonster === i;
            return (
              <div
                key={i}
                className={`comp-beast-card comp-beast-discovered ${isExpanded ? 'expanded' : ''}`}
                onClick={() => setExpandedMonster(isExpanded ? null : i)}
              >
                <div className="comp-beast-header">
                  <span className="comp-beast-icon">⚔️</span>
                  <span className="comp-beast-name">{m.name}</span>
                  {m.eliteTier && (
                    <span className={`comp-beast-tier comp-tier-${m.eliteTier}`}>
                      {m.eliteTier === 'legendary' ? '👑' : m.eliteTier === 'champion' ? '💀' : '⚠️'} {m.eliteTier.toUpperCase()}
                    </span>
                  )}
                  <span className="comp-beast-level">Lv.{m.level}</span>
                  <span className="comp-beast-kills">💀 {m.kills}</span>
                  <span className="comp-beast-expand">{isExpanded ? '▲' : '▼'}</span>
                </div>
                {isExpanded && (
                  <div className="comp-beast-details">
                    <div className="comp-beast-pic-wrap">
                      <img
                        src={`/images/monsters/${m.pic}`}
                        alt={m.name}
                        className="comp-beast-pic"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="comp-beast-stats">
                      <div className="comp-beast-stat"><span>Type</span><span>{m.type || '—'}</span></div>
                      <div className="comp-beast-stat"><span>HP</span><span>{m.hp}</span></div>
                      <div className="comp-beast-stat"><span>MP</span><span>{m.mp}</span></div>
                      <div className="comp-beast-stat"><span>ATT</span><span>{m.att}</span></div>
                      <div className="comp-beast-stat"><span>DEF</span><span>{m.def}</span></div>
                      <div className="comp-beast-stat"><span>XP</span><span>{m.xp}</span></div>
                      <div className="comp-beast-stat"><span>Killed</span><span>{m.kills}×</span></div>
                      {m.firstSeen && (
                        <div className="comp-beast-stat">
                          <span>First Seen</span>
                          <span>{new Date(m.firstSeen).toLocaleDateString()}</span>
                        </div>
                      )}
                      {/* Elemental info — unlocked after 3+ kills */}
                      {m.weaknesses !== undefined ? (
                        <div className="comp-beast-elemental">
                          {m.weaknesses.length > 0 && (
                            <div className="comp-beast-stat comp-beast-weak">
                              <span>🔥 Weak to</span>
                              <span>{m.weaknesses.join(', ')}</span>
                            </div>
                          )}
                          {m.resistances.length > 0 && (
                            <div className="comp-beast-stat comp-beast-resist">
                              <span>🛡️ Resists</span>
                              <span>{m.resistances.join(', ')}</span>
                            </div>
                          )}
                          {m.immunities.length > 0 && (
                            <div className="comp-beast-stat comp-beast-immune">
                              <span>⛔ Immune</span>
                              <span>{m.immunities.join(', ')}</span>
                            </div>
                          )}
                          {m.weaknesses.length === 0 && m.resistances.length === 0 && m.immunities.length === 0 && (
                            <div className="comp-beast-stat"><span>Elements</span><span>No special weaknesses</span></div>
                          )}
                          {m.abilities > 0 && (
                            <div className="comp-beast-stat comp-beast-abilities">
                              <span>⚔️ Abilities</span>
                              <span>{m.abilities} special {m.abilities === 1 ? 'ability' : 'abilities'}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="comp-beast-stat comp-beast-locked-hint">
                          <span>Elements</span>
                          <span>🔒 Kill {Math.max(0, 3 - (m.kills || 0))} more to reveal</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================
   *  HOW TO PLAY TAB
   * ================================================================ */
  const renderHowToPlay = () => {
    return (
      <div className="comp-help-tab">
        <div className="comp-help-section">
          <h3>⚔️ Combat & Cards</h3>
          <p>Battles use a <strong>card-based system</strong>. You draw cards each turn and spend <strong>Action Points (AP)</strong> to play them. You get 3 AP per turn.</p>
          <p>Cards include weapon attacks, spells, abilities, and items. Build your deck in the <strong>Deck Manager</strong> (🃏) to customize your combat strategy.</p>
        </div>

        <div className="comp-help-section">
          <h3>🌱 Seeds & Magic</h3>
          <p><strong>Seeds</strong> are elemental affinities that grow as you level up. Mages choose an elemental seed, Clerics follow a deity's divine seed web, and Psions cultivate a psionic discipline.</p>
          <p>As your seed grows, you unlock increasingly powerful spells along a branching web. Open your <strong>Spellbook</strong> to see every spell your seeds unlock and how close you are to the next tier.</p>
        </div>

        <div className="comp-help-section">
          <h3>⚖️ Alignment</h3>
          <p>Your alignment shifts based on your choices. Good and evil factions offer different quests and rewards.</p>
          <p>Temples may refuse to help evil characters, while darker powers become available to those who embrace wickedness. Your alignment is shown on the <strong>Character Sheet</strong> (<kbd>C</kbd>).</p>
        </div>

        <div className="comp-help-section">
          <h3>⚒️ Crafting</h3>
          <p>Three crafting disciplines: <strong>Smithing</strong> (weapons and armor), <strong>Alchemy</strong> (potions and consumables), and <strong>Enchanting</strong> (magical enhancements).</p>
          <p>Gather materials from the world and craft at workstations. Each discipline levels up independently as you craft.</p>
        </div>

        <div className="comp-help-section">
          <h3>🌿 Gathering</h3>
          <p>Some tiles in the world contain <strong>gathering nodes</strong> — herbs, ore deposits, or other resources. Walk onto them to gather materials for crafting.</p>
          <p>Gathering nodes respawn over time. Keep an eye out for patches of unusual ground!</p>
        </div>

        <div className="comp-help-section">
          <h3>🏠 Death & Homepoints</h3>
          <p>When you die, you <strong>respawn at your homepoint</strong>. You lose a small amount of experience (~200 XP per level), but you cannot drop below your current level — your <strong>gear and gold are always safe</strong>.</p>
          <p>Visit temples and shrines to set a new homepoint closer to your adventures. Your current homepoint is shown on the <strong>Character Sheet</strong> (<kbd>C</kbd>).</p>
        </div>

        <div className="comp-help-section">
          <h3>⚠️ Elite Monsters</h3>
          <p>Some overworld monsters are <strong>elite variants</strong> — stronger foes with special abilities and better loot. There are three tiers:</p>
          <p><strong style={{color:'#e67e22'}}>⚠️ Elite</strong> — Tougher with a chance for a special ability. <strong style={{color:'#9b59b6'}}>💀 Champion</strong> — Significantly stronger with guaranteed abilities and +1 DR. <strong style={{color:'#f1c40f'}}>👑 Legendary</strong> — Extremely rare and powerful with two abilities, +2 DR, and the best loot in the game.</p>
          <p>Elite monsters drop <strong>elite trophies</strong> — unique collectible materials based on the monster type. Check the <strong>Bestiary</strong> tab to track your elite kills!</p>
        </div>

        <div className="comp-help-section">
          <h3>📊 Stats & Attributes</h3>
          <p><strong>Strength</strong> — physical attack damage. <strong>Agility</strong> — speed and defense. <strong>Vitality</strong> — health and resistance. <strong>Mentality</strong> — mana and magic potency. <strong>Charisma</strong> — charm success and shop prices.</p>
          <p>Hover over stats on your <strong>Character Sheet</strong> (<kbd>C</kbd>) for detailed breakdowns.</p>
        </div>

        <div className="comp-help-section">
          <h3>🎯 Skill Trees</h3>
          <p>Press <kbd>K</kbd> to open your Skill Tree. As you level up, you earn points to unlock new abilities, spells, and passive bonuses unique to your class.</p>
        </div>

        <div className="comp-help-section comp-help-links">
          <h3>📖 Detailed Guides</h3>
          <p>Want to dive deeper? Check out the full guides:</p>
          <div className="comp-help-link-list">
            <a href="/class-guide.html" target="_blank" rel="noopener noreferrer">📕 Class Guide</a>
            <a href="/combat-guide.html" target="_blank" rel="noopener noreferrer">⚔️ Combat Guide</a>
            <a href="/crafting-guide.html" target="_blank" rel="noopener noreferrer">⚒️ Crafting Guide</a>
            {onOpenGuide && (
              <button type="button" onClick={() => onOpenGuide('overview')}>📘 Progression Guide</button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ================================================================
   *  MAIN RENDER
   * ================================================================ */
  return (
    <div className="comp-overlay" onClick={onClose}>
      <div className="comp-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="comp-header">
          <h2>📚 Compendium</h2>
          <button className="comp-close-btn" onClick={onClose}>✖</button>
        </div>

        {/* Tab bar */}
        <div className="comp-tabs">
          <button
            className={`comp-tab ${tab === 'quests' ? 'active' : ''}`}
            onClick={() => setTab('quests')}
          >
            📜 Quest Log
          </button>
          <button
            className={`comp-tab ${tab === 'recipes' ? 'active' : ''}`}
            onClick={() => setTab('recipes')}
          >
            📖 Recipe Book
          </button>
          <button
            className={`comp-tab ${tab === 'bestiary' ? 'active' : ''}`}
            onClick={() => setTab('bestiary')}
          >
            🐉 Bestiary
          </button>
          <button
            className={`comp-tab ${tab === 'help' ? 'active' : ''}`}
            onClick={() => setTab('help')}
          >
            ❓ How to Play
          </button>
        </div>

        {/* Content */}
        <div className="comp-content">
          {loading && <div className="comp-loading">Loading compendium…</div>}
          {error && <div className="comp-error">⚠️ {error}</div>}
          {!loading && !error && (
            <>
              {tab === 'quests' && renderQuestLog()}
              {tab === 'recipes' && renderRecipeBook()}
              {tab === 'bestiary' && renderBestiary()}
              {tab === 'help' && renderHowToPlay()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Compendium;
